import { useState, useEffect } from 'react'
import axios from 'axios'
import { Row, Col, Spinner } from 'reactstrap'
import { Star, ThumbsUp, MessageSquare } from 'react-feather'

const BASE = process.env.REACT_APP_BASEURL
const getToken = () => localStorage.getItem(process.env.REACT_APP_USERTOKEN)
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } })

// ─── Star Display (Read-Only) ────────────────────────────────────────────────
const StarDisplay = ({ value, size = 18 }) => {
  const num = Number(value) || 0
  return (
    <span className="d-inline-flex align-items-center" style={{ gap: '3px' }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          fill={n <= Math.round(num) ? '#ff9f43' : 'none'}
          stroke={n <= Math.round(num) ? '#ff9f43' : '#d0d2d6'}
        />
      ))}
      <span className="font-small-3 font-weight-bold ml-50 text-dark">
        ({num > 0 ? num.toFixed(1) : '0'}/5)
      </span>
    </span>
  )
}

const CumulativeRatings = () => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)

  useEffect(() => {
    let isMounted = true
    axios
      .get(`${BASE}brand/sample-feedback/overall-summary`, authHeaders())
      .then((res) => {
        if (isMounted && res.data?.success) {
          setData(res.data)
        }
      })
      .catch(() => {
        // Graceful fallback on network error or missing data
      })
      .finally(() => {
        if (isMounted) setLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [])

  if (loading) {
    return (
      <div className="figma-split-card d-flex justify-content-center align-items-center py-3">
        <Spinner color="primary" size="sm" className="mr-50" />
        <span className="text-muted font-small-3">Loading overall ratings...</span>
      </div>
    )
  }

  const avgRating = data?.avg_rating
  const recommendPct = data?.recommend_pct
  const completedCount = data?.counts?.completed ?? 0
  const ratingCount = data?.counts?.rating_count ?? completedCount
  const hasData = completedCount > 0 && (avgRating !== null || recommendPct !== null)

  return (
    <div className="figma-split-card" id="brand-dashboard-cumulative-ratings">
      <div className="sample-rating-heading">
        <div className="sample-rating-heading-icon">
          <Star size={20} />
        </div>
        <div>
          <div className="figma-section-label mb-25">OVERALL SAMPLE RATING</div>
          <p className="sample-rating-subtitle">
            Cumulative results across all sample posts and dietitian feedback
          </p>
        </div>
      </div>

      {hasData ? (
        <Row className="match-height">
          <Col xs={12} lg={5} className="mb-1 mb-lg-0">
            <div className="sample-rating-primary h-100">
              <span className="sample-rating-eyebrow">Average product rating</span>
              <div className="sample-rating-score-row">
                <span className="sample-rating-score">
                  {avgRating ? avgRating.toFixed(1) : '-'}
                </span>
                <span className="sample-rating-scale">/ 5</span>
              </div>
              <StarDisplay value={avgRating} size={20} />
              <p className="sample-rating-caption">
                Based on {ratingCount} dietitian {ratingCount === 1 ? 'review' : 'reviews'}
              </p>
            </div>
          </Col>

          <Col xs={12} lg={7}>
            <Row className="match-height h-100">
              <Col xs={12} sm={6} className="mb-1 mb-sm-0">
                <div className="sample-rating-support-metric h-100">
                  <div className="sample-rating-metric-icon is-success">
                    <ThumbsUp size={20} />
                  </div>
                  <div className="sample-rating-support-value">
                    {recommendPct !== null && recommendPct !== undefined ? `${recommendPct}%` : '-'}
                  </div>
                  <div className="sample-rating-support-label">Would recommend</div>
                  <p className="sample-rating-support-caption">
                    Dietitians who would recommend your products to clients
                  </p>
                </div>
              </Col>

              <Col xs={12} sm={6}>
                <div className="sample-rating-support-metric h-100">
                  <div className="sample-rating-metric-icon is-blue">
                    <MessageSquare size={20} />
                  </div>
                  <div className="sample-rating-support-value">{completedCount}</div>
                  <div className="sample-rating-support-label">Completed reviews</div>
                  <p className="sample-rating-support-caption">
                    Dietitian feedback surveys submitted
                  </p>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      ) : (
        <div className="sample-rating-empty-state">
          <div className="sample-rating-empty-icon">
            <Star size={24} />
          </div>
          <h5 className="font-weight-bolder text-dark mb-50">No Sample Feedback Yet</h5>
          <p className="text-muted font-small-3 mb-0">
            Overall ratings and recommendation rates will appear here once dietitians complete feedback on your sample requests.
          </p>
        </div>
      )}
    </div>
  )
}

export default CumulativeRatings
